<script setup>
import { storeToRefs } from 'pinia'
import { useMessageStore } from '~/stores/messages'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'

const props = defineProps({
    coupon: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                coupon: rawValue => typeof rawValue === 'object',
                uid: rawValue => rawValue?.uid === undefined || typeof rawValue?.uid === 'string',
                headline: rawValue => rawValue?.headline === undefined || typeof rawValue?.headline === 'string',
                subheadline: rawValue => rawValue?.subheadline === undefined || typeof rawValue?.subheadline === 'string',
                valid: rawValue => rawValue?.valid === undefined || typeof rawValue?.valid === 'object',
                from: rawValue => rawValue?.valid?.from === undefined || typeof rawValue?.valid?.from === 'string',
                till: rawValue => rawValue?.valid?.till === undefined || typeof rawValue?.valid?.till === 'string',
                validForOneDay: rawValue => rawValue?.validForOneDay === undefined || typeof rawValue?.validForOneDay === 'boolean',
                logotype: rawValue => rawValue?.logotype === undefined || typeof rawValue?.logotype === 'object',
                thumbnail: rawValue => rawValue?.logotype?.thumbnail === undefined || typeof rawValue?.logotype?.thumbnail === 'string',
                altText: rawValue => rawValue?.logotype?.altText === undefined || typeof rawValue?.logotype?.altText === 'string',
                shopIds: rawValue => rawValue?.shopIds === undefined || (Array.isArray(rawValue?.shopIds) && typeof rawValue?.shopIds === 'object'),
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue?.placeholder === 'boolean',
                trackingPayload: rawValue => rawValue?.trackingPayload === undefined || typeof rawValue?.trackingPayload === 'object',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Markdown
const { $md } = useNuxtApp()
const headline = computed(() => $md.renderInline(props.coupon?.headline || ''))
const subHeadline = computed(() => $md.renderInline(props.coupon?.subheadline || ''))

/**
 * Computed property that calculates the valid till date based on certain conditions.
 */
const validDate = computed(() => {
    const shortDate = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: 'UTC' })
    const validTill = props.coupon?.valid?.till
    const validForOneDay = props.coupon?.validForOneDay
    const validTillLabel = staticKeys.value?.coupons_valid_until?.replace(/\s+$/, '')
    const validOnLabel = staticKeys.value?.coupons_valid_on?.replace(/\s+$/, '')

    return validTill ? `${validForOneDay ? validOnLabel : validTillLabel}: ${shortDate.format(new Date(validTill))}` : null
})

// Truncate text
const getTruncatedDescription = computed(() => insertEllipsis(subHeadline.value, 60))

// Get loading class
const getCouponListItemClass = computed(() => {
    return {
        'skeleton-placeholder-container': props?.coupon?.placeholder
    }
})

const emit = defineEmits(['openModal'])

/**
 * Handle coupon click: Open modal and track click if applicable
 */
const handleClick = () => {
    if (!props.coupon?.placeholder && props.coupon?.trackingPayload) {
        // Track user click
        trackUserClick({
            eventName: TRACKING_CONSTANTS.CLICK_EVENTS.COUPON_CLICK,
            ...props.coupon?.trackingPayload
        })
    }

    // Open modal
    emit('openModal')
}
</script>

<template>
    <li :class="getCouponListItemClass" :data-coupon-item="coupon.index" @click="handleClick()">
        <ShadowContentBox class="coupon-list-item" :shadow-content-box-config="{ type: 'div' }">
            <div class="coupon-list-item-thumbnail-container">
                <component
                    :is="SkeletonPlaceholder"
                    v-if="coupon.placeholder"
                    :skeleton-placeholder-config="{ type: 'image', width: '80px', height: '80px' }"
                />

                <picture v-else-if="isTypeImage(coupon?.logotype?.thumbnail)">
                    <RespImgSrc
                        tag-type="source"
                        :image-src="coupon?.logotype?.thumbnail"
                        :srcset="[80]"
                        :format="['avif', 'webp', 'jpg']"
                        :width="80"
                        :height="80"
                        sizes="80px"
                    />
                    <RespImgSrc
                        tag-type="img"
                        :image-src="coupon?.logotype?.thumbnail"
                        :srcset="[80]"
                        :dpr="[1]"
                        sizes="116px"
                        :format="['jpg']"
                        :width="80"
                        :height="80"
                        class="coupon-list-item-thumbnail"
                        :additional-attrs="{ alt: coupon?.logotype?.altText || 'Coupon image' }"
                    />
                </picture>
            </div>

            <div class="coupon-list-item-content">
                <template v-if="coupon.placeholder">
                    <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'multi-headline', tag: 'h3' }" />
                    <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text', iterator: 1 }" />
                </template>

                <template v-else>
                    <p class="coupon-list-item-headline" :title="coupon?.headline" v-html="headline" />

                    <p
                        v-if="getTruncatedDescription"
                        class="coupon-list-item-subheadline"
                        :title="coupon?.subheadline"
                        v-html="getTruncatedDescription"
                    />

                    <p v-if="coupon?.valid?.till" class="coupon-list-item-valid">
                        {{ validDate }}
                    </p>
                </template>
            </div>
        </ShadowContentBox>
    </li>
</template>

<style lang="scss">
.coupon-list-item {
    position: relative;
    display: grid;
    grid-template-columns: 136px 1fr;
    grid-template-areas: "thumbnail content";
    align-items: flex-start;
    text-align: center;
    height: px2rem(150);

    img {
        display: block;
        max-width: 100%;
    }
}

.coupon-list-item-thumbnail-container {
    grid-area: thumbnail;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    isolation: isolate;
    mix-blend-mode: multiply;
}

.coupon-list-item-thumbnail {
    width: 80px;
    height: calc(100% - 38px);
    max-height: calc(100% - 58px);
    max-width: calc(100% - 58px);
    object-fit: contain;

}

.coupon-list-item-content {
    grid-area: content;
    display: grid;
    grid-template: "headline" auto "subheadline" 1fr "valid" auto / 1fr;
    text-align: left;
    height: 100%;
    padding: 1rem 1rem 1rem 0;

    & > * {
        margin: 0;
    }
}

.coupon-list-item-headline {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 100%;

    @include h4-semibold;

    @include bp(large1) {
        @include body1-semibold;
    }
}

.coupon-list-item-subheadline {
    font-family: $font-body;
    font-weight: 500;
    font-size: px2rem(14);
    line-height: px2rem(20);
    align-self: center;
    margin: 0;
}

.coupon-list-item-valid {
    margin-block-start: auto;
    color: $valid-txt;

    @include body2-semibold;
}
</style>
